import * as React from 'react';

import TabListExtraMenu from './TabListExtraMenu';
import TabListNavItem from './TabListNavItem';

interface Props {
    initialActiveKey: string;
    maxNavItemsShow: number;
    className: string;
    onTabSwitch?: (activeTabKey: string | null, newTabKey: string | null) => void;
    clickActiveTabToHide: boolean;
    enableEventTrapping: boolean;
    children?: React.ReactNode;
}

interface State {
    activeTabKey: string | null;
    extraMenuActive: boolean;
}

export default class TabList extends React.PureComponent<Props, State> {
    static defaultProps = {
        className: '',
        clickActiveTabToHide: false,
        maxNavItemsShow: 7,
        enableEventTrapping: true,
    };

    constructor(props: Props) {
        super(props);

        const { initialActiveKey } = props;

        this.state = {
            activeTabKey: initialActiveKey,
            extraMenuActive: false,
        };
    }

    componentDidMount() {
        document.addEventListener('click', this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const { children } = this.props;

        const tabs = React.Children.toArray(children);
        const tabKeys: Array<string | null> = React.Children.map(tabs, (child) => {
            if (this.isReactElementLike(child)) {
                return child ? child.props.tkey : null;
            }
        });

        if (!prevProps.clickActiveTabToHide && !tabKeys.includes(prevState.activeTabKey)) {
            this.setState((prevState) => ({
                activeTabKey: tabKeys[0],
            }));
        }
    }

    handleNavClick = (clickedTabKey: string): void => {
        const { activeTabKey } = this.state;
        const { onTabSwitch, clickActiveTabToHide } = this.props;

        let newTabKey: string | null = clickedTabKey;
        if (clickActiveTabToHide) {
            newTabKey = activeTabKey === clickedTabKey ? null : clickedTabKey;
        }

        if (onTabSwitch) {
            onTabSwitch(activeTabKey, newTabKey);
        }

        this.setState({
            activeTabKey: newTabKey,
            extraMenuActive: false,
        });
    };

    handleExtraMenuClick = (evt: React.MouseEvent): void => {
        evt.nativeEvent.stopImmediatePropagation();

        this.setState((prevState, props) => ({
            extraMenuActive: !prevState.extraMenuActive,
        }));
    };

    handleDocumentClick = (): void => {
        const { extraMenuActive } = this.state;

        if (extraMenuActive) {
            this.setState({
                extraMenuActive: false,
            });
        }
    };

    isReactElementLike = (component: React.ReactNode): component is React.ReactElement => {
        return !!component && typeof component === 'object' && (component as React.ReactElement).type !== 'undefined';
    };

    render() {
        const { activeTabKey, extraMenuActive } = this.state;
        const { className, children, maxNavItemsShow } = this.props;

        const tabs = React.Children.toArray(children);

        const visibleTabs = tabs.slice(0, maxNavItemsShow);
        const extraTabs = tabs.slice(maxNavItemsShow);

        const classNames: Array<string> = ['ddbc-tab-list', className];

        return (
            <div className={classNames.join(' ')}>
                <div className="ddbc-tab-list__nav">
                    {visibleTabs.map((t) => {
                        if (this.isReactElementLike(t)) {
                            const { tkey } = t.props;
                            return (
                                <TabListNavItem
                                    key={tkey}
                                    {...t.props}
                                    isActive={tkey === activeTabKey}
                                    onItemClick={this.handleNavClick}
                                />
                            );
                        }
                    })}
                    <TabListExtraMenu
                        isActive={extraMenuActive}
                        items={extraTabs}
                        activeTabKey={activeTabKey}
                        onExtraMenuClick={this.handleExtraMenuClick}
                        onItemClick={this.handleNavClick}
                    />
                </div>
                <div className="ddbc-tab-list__content">
                    {React.Children.map(children, (child) => {
                        if (this.isReactElementLike(child)) {
                            return child && child.props.tkey === activeTabKey ? child : null;
                        }
                    })}
                </div>
            </div>
        );
    }
}
